import { ChallengeService } from '@duck/api-definitions'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  MarkdownEditor,
  Button,
} from '@duck/ui'
import { WysiwygEditor } from '@/components/WysiwygEditor/WysiwygEditor'
import { useTar } from '@/modules'
import { useEffect, useState, type ComponentType } from 'react'
import { SubmitUserFeedback } from './SubmitUserFeedback'
import { GradeBadge } from '@/components/GradeBadge/GradeBadge'
import { RelativeDateTime } from '../datetime'

type ViewMode = 'feedback' | 'solution' | 'submitFeedback'

interface FeedbackOverviewProps {
  isOpen: boolean
  onClose: () => void
  challenge: CompletedChallenge
  onShareFeedback?: (challenge: CompletedChallenge) => void
  onFeedbackSubmitted?: (feedback: ChallengeService.UserFeedback) => void
  initialViewMode?: ViewMode
}

export interface CompletedChallenge extends ChallengeService.ChallengeInfo {
  icon?: ComponentType<{ className?: string }>
  estimatedTime?: string
}

export const FeedbackDialog = ({
  isOpen,
  onClose,
  challenge,
  onShareFeedback,
  onFeedbackSubmitted,
  initialViewMode = 'feedback'
}: FeedbackOverviewProps) => {
  const { t } = useTar()
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode)
  const [userFeedbackSubmitted, setUserFeedbackSubmitted] = useState<boolean>(!!challenge.challenge.userFeedback)

  useEffect(() => {
    if (isOpen) {
      setViewMode(initialViewMode)
    }
  }, [isOpen, initialViewMode])

  if (!challenge) {
    return null
  }

  const { feedbackText, feedbackGrade } = challenge.challenge
  const { task } = challenge

  const handleToggleView = () => {
    setViewMode((current) => (current === 'solution' ? 'feedback' : 'solution'))
  }

  const handleShareFeedbackClick = () => {
    onShareFeedback?.(challenge)
    setViewMode('submitFeedback')
  }

  const handleFeedbackSubmissionSuccess = (feedback: ChallengeService.UserFeedback) => {
    setUserFeedbackSubmitted(true)
    setViewMode('feedback')
    onFeedbackSubmitted?.(feedback)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <CheckCircleIcon className="w-8 h-8 text-success" />
              <div>
                <DialogTitle className="text-xl">
                  {viewMode === 'submitFeedback'
                    ? t('userFeedback.evaluateTheFeedback')
                    : t('challenges.resultsTitle')}
                </DialogTitle>
                <p className="text-sm text-muted-foreground">{task.name}</p>
              </div>
            </div>
            {viewMode === 'submitFeedback' && (
              <Button
                variant="outline"
                size="sm"
                className="me-4"
                onClick={() => setViewMode('feedback')}
              >
                {t('userFeedback.backToResults')}
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="space-y-6 overflow-y-auto custom-scrollbar pr-2 max-h-[calc(90vh-200px)]">
          {viewMode === 'solution' ? (
            /* Solution View */
            <div className="h-full">
              <WysiwygEditor
                content={challenge.challenge.workState?.solution || ''}
                startingCharts={challenge.challenge.workState?.charts || {}}
                readOnly={true}
                className="h-full"
              />
            </div>
          ) : viewMode === 'submitFeedback' ? (
            <SubmitUserFeedback
              key={challenge.challenge.id}
              challengeId={challenge.challenge.id}
              onCancel={() => setViewMode('feedback')}
              onSuccess={handleFeedbackSubmissionSuccess}
            />
          ) : (
            /* Original Feedback Content */
            <>
              {/* Grade Section */}
              {feedbackGrade !== undefined && (
                <div className="flex justify-center w-full">
                  <div className="bg-accent/5 rounded-lg px-8 py-8 border border-accent/40 w-full max-w-md">
                    <div className="flex justify-center flex-col">
                      <div className="flex items-center justify-center mb-3 w-full">
                        <h3 className="text-lg font-semibold text-foreground">
                          {t('challenges.yourScore')}
                        </h3>
                      </div>
                      <div className="flex items-center justify-center w-full">
                        <GradeBadge grade={feedbackGrade} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Feedback Text Section */}
              <div className="">
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  {t('challenges.aiFeedback')}
                </h3>
                <MarkdownEditor
                  defaultValue={feedbackText}
                  className="w-full h-auto overflow-y-auto custom-scrollbar"
                  mode="view"
                />
              </div>

              {/* Challenge Details */}
              <div className="bg-muted/5 rounded-lg p-4 border border-border">
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  {t('challenges.challengeDetails')}
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">
                      {t('challenges.language')}:
                    </span>
                    <span className="ml-2 font-medium text-foreground">
                      {task.language}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">
                      {t('challenges.difficulty')}:
                    </span>
                    <span className="ml-2 font-medium text-foreground">
                      {task.difficulty}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">
                      {t('common.workTime')}:
                    </span>
                    <span className="ml-2 font-medium text-foreground">
                      {t('times.xMinutes', { x: challenge.challenge.workTime })}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">
                      {t('challenges.completed')}:
                    </span>
                    <span className="ml-2 font-medium text-foreground">
                      <RelativeDateTime
                        isoDateString={challenge.challenge.endTime}
                        fallbackText={t('times.invalidDate')}
                      />
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {viewMode !== 'submitFeedback' && (
          <DialogFooter className="pt-4">
            <div className="flex items-center w-full">
              {!userFeedbackSubmitted && (
                <Button variant="outline" onClick={handleShareFeedbackClick}>
                  {t('userFeedback.evaluateTheFeedback')}
                </Button>
              )}
              <Button
                variant="outline"
                className="ml-auto"
                onClick={handleToggleView}
              >
                {t(
                  viewMode === 'solution'
                    ? 'challenges.viewFeedback'
                    : 'challenges.viewSubmittedSolution'
                )}
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
